/**
 * TextSourceDrawer
 *
 * In-app preview for plain-text and manual ("Textinhalt") chat sources. Clicking
 * such a source in the citations list opens this side drawer rendering the
 * document's text inline -- styled, scrollable, with the cited passage
 * highlighted and scrolled into view -- instead of opening a raw-text browser
 * tab (the old behavior, which also broke for manual sources that have no file).
 *
 * Fetches GET /api/documents/{id}/content via the raw apiClient.request (the
 * endpoint is additive and not in the regenerated typed client, like
 * useUploadCapabilities).
 */

import React, { useEffect, useRef, useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import { Close as CloseIcon, ArticleOutlined as ArticleOutlinedIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../../config/apiConfig';
import { log } from '../../utils/errors/logger';
import { squashForMatch, squashSpanText } from './PdfPassageDrawer';

interface DocumentContentResponse {
  document_id: string;
  title: string | null;
  source_type: string | null;
  file_type: string | null;
  content: string;
  truncated: boolean;
  length: number;
}

interface FigureTableData {
  columns?: string[];
  rows?: string[][];
  num_rows?: number;
  num_cols?: number;
}

interface FigureResult {
  figure_id: string;
  figure_type: string;
  table_data?: FigureTableData | null;
  summary?: string | null;
}

interface DocumentFiguresResponse {
  document_id: string;
  figures: FigureResult[];
  figure_count: number;
}

export interface TextSourceTarget {
  documentId: string;
  title?: string;
  /** The cited passage to highlight within the full text, if known. */
  excerpt?: string | null;
}

interface TextSourceDrawerProps {
  target: TextSourceTarget | null;
  onClose: () => void;
}

/**
 * Split `text` into segments, marking the cited excerpt so it can be visually
 * highlighted. Matching uses the same squash-map anchoring as the PDF viewer
 * (whitespace/dash/quote-insensitive, NFD-folded) because the excerpt is a
 * markdown-cleaned, whitespace-collapsed form of the raw document text —
 * exact `indexOf` rarely matches and a fixed-length prefix cuts mid-word.
 * The highlight maps back to ORIGINAL character offsets; a partial hit is
 * extended to the longest matching run and completed to the word end.
 */
export function buildHighlightedParts(
  text: string,
  excerpt?: string | null
): Array<{ text: string; highlight: boolean }> {
  const needle = squashForMatch(excerpt ?? '');
  if (needle.length < 12) {
    return [{ text, highlight: false }];
  }
  const { piece, map } = squashSpanText(text);

  let at = piece.indexOf(needle);
  let len = needle.length;
  if (at < 0) {
    for (const l of [160, 80, 40]) {
      if (needle.length > l) {
        const prefixAt = piece.indexOf(needle.slice(0, l));
        if (prefixAt >= 0) {
          at = prefixAt;
          len = l;
          break;
        }
      }
    }
  }
  if (at < 0) {
    return [{ text, highlight: false }];
  }
  // Extend a prefix hit to the longest run of the full excerpt that matches.
  while (at + len < piece.length && len < needle.length && piece[at + len] === needle[len]) {
    len += 1;
  }
  // A partial match ends at an arbitrary character — complete the trailing
  // word (bounded) so the highlight never stops mid-word.
  if (len < needle.length) {
    let guard = 24;
    while (at + len < piece.length && guard > 0 && /[\p{L}\p{N}]/u.test(piece[at + len])) {
      len += 1;
      guard -= 1;
    }
  }
  const start = map[at];
  const end = Math.min(map[at + len - 1] + 1, text.length);
  return [
    { text: text.slice(0, start), highlight: false },
    { text: text.slice(start, end), highlight: true },
    { text: text.slice(end), highlight: false },
  ];
}

/** Renders any extracted structured tables as real HTML tables. */
const FigureTables: React.FC<{ figures: FigureResult[]; heading: string }> = ({ figures, heading }) => {
  const tables = figures.filter((f) => f.table_data && (f.table_data.rows?.length ?? 0) > 0);
  if (tables.length === 0) {
    return null;
  }
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 600 }}>
        {heading}
      </Typography>
      {tables.map((f) => (
        <Box
          key={f.figure_id}
          component="table"
          sx={{
            width: '100%',
            mt: 1,
            mb: 2,
            borderCollapse: 'collapse',
            fontSize: '0.85rem',
            '& th, & td': { border: 1, borderColor: 'divider', px: 1, py: 0.5, textAlign: 'left' },
            '& th': { bgcolor: 'action.hover', fontWeight: 600 },
          }}
        >
          {f.table_data?.columns && f.table_data.columns.length > 0 && (
            <Box component="thead">
              <Box component="tr">
                {f.table_data.columns.map((c, ci) => (
                  <Box component="th" key={`c${ci}:${c}`}>
                    {c}
                  </Box>
                ))}
              </Box>
            </Box>
          )}
          <Box component="tbody">
            {(f.table_data?.rows ?? []).map((row, ri) => (
              <Box component="tr" key={`r${ri}`}>
                {row.map((cell, ci) => (
                  <Box component="td" key={`r${ri}c${ci}`}>
                    {cell}
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

interface DrawerBodyProps {
  loading: boolean;
  error: string | null;
  data: DocumentContentResponse | null;
  parts: Array<{ text: string; highlight: boolean }>;
  highlightRef: React.RefObject<HTMLSpanElement | null>;
  citedLabel: string;
  truncatedLabel: string;
}

/** The scrollable content area: loading / error / cited callout + readable text. */
const DrawerBody: React.FC<DrawerBodyProps> = ({
  loading,
  error,
  data,
  parts,
  highlightRef,
  citedLabel,
  truncatedLabel,
}) => {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }
  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }
  if (!data) {
    return null;
  }

  const citedPassage = parts.find((p) => p.highlight)?.text.trim();

  return (
    <Box sx={{ maxWidth: '68ch', mx: 'auto' }}>
      {/* The passage used for the answer, shown once as a quoted callout. */}
      {citedPassage && (
        <Box
          ref={highlightRef}
          sx={{
            mb: 3,
            p: 2,
            borderLeft: 3,
            borderColor: 'primary.main',
            borderRadius: 1,
            bgcolor: (theme) =>
              theme.palette.mode === 'dark' ? 'rgba(144,202,249,0.08)' : 'rgba(21,101,192,0.05)',
          }}
        >
          <Typography
            variant="overline"
            sx={{ color: 'primary.main', fontWeight: 600, letterSpacing: 0.5 }}
          >
            {citedLabel}
          </Typography>
          <Typography sx={{ fontSize: '0.92rem', lineHeight: 1.65, color: 'text.primary' }}>
            {citedPassage}
          </Typography>
        </Box>
      )}

      {/* Full document text in comfortable reading typography. The cited passage
          is marked subtly inline so it can still be located in context. */}
      <Typography
        component="div"
        sx={{
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          fontSize: '0.95rem',
          lineHeight: 1.75,
          color: 'text.secondary',
        }}
      >
        {parts.map((part) => {
          const key = `${part.highlight ? 'h' : 'n'}:${part.text.slice(0, 24)}:${part.text.length}`;
          return part.highlight ? (
            <Box
              key={key}
              component="mark"
              sx={{
                bgcolor: (theme) =>
                  theme.palette.mode === 'dark' ? 'rgba(255,213,79,0.20)' : 'rgba(255,213,79,0.45)',
                color: 'text.primary',
                borderRadius: 0.5,
                px: 0.25,
              }}
            >
              {part.text}
            </Box>
          ) : (
            <React.Fragment key={key}>{part.text}</React.Fragment>
          );
        })}
      </Typography>
      {data.truncated && (
        <Alert severity="info" sx={{ mt: 2 }}>
          {truncatedLabel}
        </Alert>
      )}
    </Box>
  );
};

export const TextSourceDrawer: React.FC<TextSourceDrawerProps> = ({ target, onClose }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<DocumentContentResponse | null>(null);
  const [figures, setFigures] = useState<FigureResult[]>([]);
  const highlightRef = useRef<HTMLSpanElement | null>(null);

  const open = target !== null;

  useEffect(() => {
    if (!target) {
      return;
    }
    let cancelled = false;
    const docId = encodeURIComponent(target.documentId);
    setLoading(true);
    setError(null);
    setData(null);
    setFigures([]);
    apiClient
      .request<DocumentContentResponse>({
        path: `/api/documents/${docId}/content`,
        method: 'GET',
        secure: true,
        format: 'json',
      })
      .then((res) => {
        if (!cancelled) {
          setData(res.data);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          log.error('Failed to load text source content', { error: err });
          setError(t('chat.textSource.loadError', 'Inhalt konnte nicht geladen werden.'));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    // Figures are a non-blocking bonus: structured tables extracted at ingest.
    apiClient
      .request<DocumentFiguresResponse>({
        path: `/api/documents/${docId}/figures`,
        method: 'GET',
        secure: true,
        format: 'json',
      })
      .then((res) => {
        if (!cancelled) {
          setFigures(res.data.figures ?? []);
        }
      })
      .catch(() => {
        /* figures are optional; ignore failures */
      });
    return () => {
      cancelled = true;
    };
  }, [target, t]);

  // Scroll the highlighted passage into view once content is rendered.
  useEffect(() => {
    if (data && highlightRef.current) {
      highlightRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [data]);

  const parts = data ? buildHighlightedParts(data.content, target?.excerpt) : [];

  return (
    <Drawer anchor="right" open={open} onClose={onClose} sx={{ zIndex: (theme) => theme.zIndex.modal }}>
      <Box sx={{ width: { xs: '100vw', sm: 480, md: 560 }, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
            <ArticleOutlinedIcon sx={{ color: 'text.secondary', flexShrink: 0 }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" noWrap title={target?.title} sx={{ fontWeight: 600 }}>
                {target?.title ?? t('chat.textSource.title', 'Quelle')}
              </Typography>
              {data?.source_type === 'manual' && (
                <Chip label="Textinhalt" size="small" variant="outlined" sx={{ mt: 0.5, height: 20 }} />
              )}
            </Box>
          </Box>
          <IconButton onClick={onClose} edge="end" aria-label={t('common.close', 'Schliessen')}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ flex: 1, overflow: 'auto', px: 3, py: 3 }}>
          <Box sx={{ maxWidth: '68ch', mx: 'auto' }}>
            <FigureTables
              figures={figures}
              heading={t('chat.textSource.extractedTables', 'Extrahierte Tabellen')}
            />
          </Box>
          <DrawerBody
            loading={loading}
            error={error}
            data={data}
            parts={parts}
            highlightRef={highlightRef}
            citedLabel={t('chat.textSource.citedPassage', 'Fuer die Antwort verwendet')}
            truncatedLabel={t('chat.textSource.truncated', 'Vorschau gekuerzt (Dokument ist sehr lang).')}
          />
        </Box>
      </Box>
    </Drawer>
  );
};
